// Device breakdown for Analytics — iOS app models from Firestore
import { useMemo, useState } from "react";
import {
  computeDeviceAnalytics,
  downloadDeviceSegmentCsv,
  DEVICE_SEGMENT_EXPORT_KEYS,
} from "../lib/deviceAnalyticsMetrics";
import { segmentDeviceOs, segmentDeviceModel, hasDeviceModelReported } from "../lib/deviceProfile";
import CohortCard from "./CohortCard";
import SegmentCard from "./SegmentCard";
import AlertBanner from "./AlertBanner";
import { Smartphone, Tablet, Users, Minus } from "lucide-react";

function fmtPct(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(1)}%`;
}

export default function DeviceAnalyticsSection({ contacts, onDrill }) {
  const [includeInternal, setIncludeInternal] = useState(false);
  const deviceOpts = useMemo(() => ({ includeInternal }), [includeInternal]);
  const device = useMemo(() => computeDeviceAnalytics(contacts, deviceOpts), [contacts, deviceOpts]);

  const contactsWithModel = useMemo(
    () =>
      contacts.filter(
        (c) => (includeInternal || !c.isInternal) && hasDeviceModelReported(c)
      ),
    [contacts, includeInternal]
  );

  const drillHandler = (deviceKey, title) => {
    if (!onDrill) return;
    onDrill({
      type: "device_drill",
      deviceKey,
      title: `Device · ${title}`,
      includeInternal,
    });
  };

  const lastSyncLabel =
    device.lastDeviceSyncMs > 0
      ? ` · last sync ${new Date(device.lastDeviceSyncMs).toLocaleString()}`
      : "";

  const coverageSub =
    device.internalWithModel > 0 && !includeInternal
      ? `${fmtPct(device.coveragePct)} of customer sign-ups · ${device.internalWithModel} internal synced (excluded)${lastSyncLabel}`
      : `${fmtPct(device.coveragePct)} of ${includeInternal ? "all accounts" : "sign-ups"} have a reported device model${lastSyncLabel}`;

  const cohortCards = [
    {
      key: "coverage",
      icon: Users,
      label: includeInternal ? "Device model coverage (all)" : "Device model coverage",
      value: `${device.withDeviceModel}/${device.totalSignUps}`,
      sub: coverageSub,
      drill: device.withDeviceModel
        ? {
            type: "device_drill",
            deviceKey: "with_model",
            title: "Accounts with device model",
            includeInternal,
          }
        : null,
      exportKey: null,
    },
    {
      key: "iphone_pro",
      icon: Smartphone,
      label: "iPhone Pro sign-ups",
      value: String(device.iphonePro),
      sub: `${fmtPct(device.iphoneProPct)} of ${includeInternal ? "all accounts" : "sign-ups"}`,
      drill: {
        type: "device_drill",
        deviceKey: "iphone_pro",
        title: "iPhone Pro sign-ups",
        includeInternal,
      },
      exportKey: "iphone_pro",
    },
    {
      key: "ipad_pro",
      icon: Tablet,
      label: "iPad Pro sign-ups",
      value: String(device.ipadPro),
      sub: `${fmtPct(device.ipadProPct)} of ${includeInternal ? "all accounts" : "sign-ups"}`,
      drill: {
        type: "device_drill",
        deviceKey: "ipad_pro",
        title: "iPad Pro sign-ups",
        includeInternal,
      },
      exportKey: "ipad_pro",
    },
    {
      key: "non_iphone_pro",
      icon: Minus,
      label: "Non–iPhone Pro sign-ups",
      value: String(device.nonIphonePro),
      sub: `${fmtPct(device.nonIphoneProPct)} of ${includeInternal ? "all accounts" : "sign-ups"}`,
      drill: {
        type: "device_drill",
        deviceKey: "non_iphone_pro",
        title: "Non–iPhone Pro sign-ups",
        includeInternal,
      },
      exportKey: "non_iphone_pro",
    },
    {
      key: "non_ipad_pro",
      icon: Minus,
      label: "Non–iPad Pro sign-ups",
      value: String(device.nonIpadPro),
      sub: `${fmtPct(device.nonIpadProPct)} of ${includeInternal ? "all accounts" : "sign-ups"}`,
      drill: {
        type: "device_drill",
        deviceKey: "non_ipad_pro",
        title: "Non–iPad Pro sign-ups",
        includeInternal,
      },
      exportKey: "non_ipad_pro",
    },
  ];

  const showInternalSyncBanner =
    !includeInternal && device.internalWithModel > 0 && device.withDeviceModel === 0;

  return (
    <div className="analytics-section card">
      <div className="chart-card-header">
        <div style={{ flex: 1 }}>
          <h2 className="text-h3">Device details</h2>
          <p className="page-header-desc" style={{ marginBottom: 0 }}>
            Counts use app-reported models from Firestore (<code>users</code>). Customer sign-ups exclude internal
            accounts.
          </p>
        </div>
        <label className="segment-card-hide-unset" style={{ marginLeft: 16, whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
          />
          Include internal accounts
        </label>
      </div>

      {showInternalSyncBanner ? (
        <AlertBanner variant="info" className="analytics-section" style={{ marginTop: 12 }}>
          Device sync is working in Firestore for {device.internalWithModel} internal account
          {device.internalWithModel === 1 ? "" : "s"} (e.g. team testing). Customer counts stay at zero until App
          Store users open the updated app. Enable <strong>Include internal accounts</strong> to preview synced
          devices.
        </AlertBanner>
      ) : null}

      <div className="stats-grid" style={{ marginTop: 12 }}>
        {cohortCards.map((k) => (
          <CohortCard
            key={k.key}
            label={k.label}
            value={k.value}
            sub={k.sub}
            icon={k.icon}
            onDrill={k.drill ? () => onDrill?.(k.drill) : undefined}
            onExport={
              k.exportKey && DEVICE_SEGMENT_EXPORT_KEYS.has(k.exportKey)
                ? () => downloadDeviceSegmentCsv(k.exportKey, contacts, deviceOpts)
                : undefined
            }
          />
        ))}
      </div>

      {device.withDeviceModel === 0 ? (
        <p className="text-caption" style={{ marginTop: 16 }}>
          No device models in this view yet. They appear when users open the Scanbrix app after updating — each login
          syncs model and OS to Firestore.
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
            marginTop: 20,
          }}
        >
          <SegmentCard
            title="Device model"
            contacts={contactsWithModel}
            labelFn={segmentDeviceModel}
            onDrill={onDrill ? drillHandler : undefined}
            drillKey="model"
            excludeInternal={!includeInternal}
          />
          <SegmentCard
            title="iOS version"
            contacts={contactsWithModel}
            labelFn={segmentDeviceOs}
            onDrill={onDrill ? drillHandler : undefined}
            drillKey="os"
            excludeInternal={!includeInternal}
          />
        </div>
      )}
    </div>
  );
}
